import { type AlbumModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type AlbumsSortState } from '../../lib/albums-query-model'
import {
  $albumsTablePage,
  $albumsTablePageSize,
  $albumsTableSort,
  albumsTablePageChanged,
  albumsTablePageReset,
  albumsTablePageSizeChanged,
  albumsTableQuery,
  albumsTableSortChanged,
  albumsTableSortReset,
} from '../../model/albums-table.model'
import { useAlbumsTableColumns } from './useAlbumsTableColumns'

interface AlbumsTableProps {
  onEdit: (albumId: string) => void
}
export const AlbumsTable = ({ onEdit }: AlbumsTableProps) => {
  const { data, pending } = useUnit(albumsTableQuery)

  const sort = useUnit($albumsTableSort)
  const changeSort = useUnit(albumsTableSortChanged)
  const resetSort = useUnit(albumsTableSortReset)

  const page = useUnit($albumsTablePage)
  const changePage = useUnit(albumsTablePageChanged)
  const resetPage = useUnit(albumsTablePageReset)
  const pageSize = useUnit($albumsTablePageSize)
  const changePageSize = useUnit(albumsTablePageSizeChanged)

  const tableColumns: TableProps<AlbumModel>['columns'] = useAlbumsTableColumns(
    { onEdit, sort },
  )

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<AlbumModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as AlbumsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Альбомов на странице' },
        }}
      />
    </TableWrapper>
  )
}
